"use client";

import { IconKey } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { useMemo, useState } from "react";
import { CreateApiKeyDialog } from "@/components/api-keys/create-api-key-dialog";
import { IntegrationCodeSheet } from "@/components/integration-code/integration-code-sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";
import { orgRoleCanCreateProjects, orgRoleCanMutateKeys } from "@/lib/org/org-constants";
import { cn } from "@/lib/utils";

interface DashboardHeaderProps {
  firstName: string;
  className?: string;
}

const dashboardProjectsDocument = gql`
  query DashboardHeaderProjects {
    me {
      org {
        projects {
          id
        }
      }
    }
  }
`;

type DashboardProjectsQuery = {
  me: {
    org: {
      projects: Array<{ id: string }>;
    } | null;
  };
};

const greetingForHour = (hour: number): string => {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

export const DashboardHeader = ({ firstName, className }: DashboardHeaderProps) => {
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const { orgRole } = useAuth();
  const canMutateKeys = orgRoleCanMutateKeys(orgRole);
  const canCreateProjects = orgRoleCanCreateProjects(orgRole);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [integrationCodeOpen, setIntegrationCodeOpen] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<{ name: string; rawKey: string } | null>(null);
  const projectsQuery = useGraphqlQuery<DashboardProjectsQuery>({
    queryKey: ["dashboardHeaderProjects"],
    document: dashboardProjectsDocument,
    enabled: canMutateKeys && !canCreateProjects,
  });
  const hasProjects = (projectsQuery.data?.me.org?.projects.length ?? 0) > 0;
  const projectsPending = Boolean(canMutateKeys && !canCreateProjects && projectsQuery.isPending);
  const showGetApiKey = canMutateKeys && (canCreateProjects || hasProjects || projectsPending);

  return (
    <header className={cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", className)}>
      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        {greeting}, {firstName}
      </h1>
      <div className="flex flex-wrap items-center gap-2">
        {canMutateKeys ? (
          <>
            <IntegrationCodeSheet
              open={integrationCodeOpen}
              onOpenChange={(open) => {
                setIntegrationCodeOpen(open);
                if (!open) setCreatedApiKey(null);
              }}
              newApiKey={createdApiKey}
            />
            {showGetApiKey ? (
              <Button onClick={() => setCreateDialogOpen(true)} disabled={projectsPending && !hasProjects}>
                <IconKey className="size-4" />
                Get API key
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {showGetApiKey ? (
        <CreateApiKeyDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={(apiKey) => {
            setCreatedApiKey(apiKey);
            setIntegrationCodeOpen(true);
          }}
        />
      ) : null}
    </header>
  );
};
